import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { StripeProvider } from '../../contexts/StripeContext';
import StripePaymentForm from '../../components/payment/StripePaymentForm';
import { paymentService } from '../../services/payments';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import AppIcon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

const BookingCheckout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentData, setPaymentIntentData] = useState(null);

  // Get booking details from URL params or localStorage
  const packageId = searchParams?.get('package_id');
  const variantId = searchParams?.get('variant_id');
  const travelers = parseInt(searchParams?.get('travelers')) || 1;
  const enquiryId = searchParams?.get('enquiry_id');

  useEffect(() => {
    initializeBooking();
  }, [packageId, variantId, travelers]);

  const initializeBooking = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        navigate('/login');
        return;
      }

      // Get user profile
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (profileError) {
        throw new Error('Failed to load user profile');
      }

      // Get travel package and variant details
      let packageQuery = supabase?.from('travel_packages')?.select(`
          *,
          package_variants (*)
        `)?.eq('is_active', true);

      if (packageId) {
        packageQuery = packageQuery?.eq('id', packageId);
      }

      const { data: packages, error: packageError } = await packageQuery?.limit(1)?.single();

      if (packageError || !packages) {
        throw new Error('Travel package not found');
      }

      const selectedVariant = variantId 
        ? packages?.package_variants?.find(v => v?.id === variantId)
        : packages?.package_variants?.[0];

      if (!selectedVariant) {
        throw new Error('Package variant not available');
      }

      // Calculate booking totals
      const unitPrice = selectedVariant?.price || packages?.base_price;
      const bookingItems = [{
        package_id: packages?.id,
        variant_id: selectedVariant?.id,
        name: `${packages?.name} - ${selectedVariant?.name}`,
        unit_price: unitPrice,
        traveler_count: travelers,
        total_price: unitPrice * travelers,
        traveler_details: {}
      }];

      const totals = paymentService?.calculateBookingTotal(bookingItems);

      const bookingDetails = {
        package: packages,
        variant: selectedVariant,
        items: bookingItems,
        ...totals,
        destination: packages?.destination,
        departure_date: selectedVariant?.departure_date,
        return_date: selectedVariant?.return_date,
        enquiry_id: enquiryId
      };

      // Prepare customer information for Stripe
      const customer = {
        userId: user?.id,
        firstName: profile?.full_name?.split(' ')?.[0] || '',
        lastName: profile?.full_name?.split(' ')?.slice(1)?.join(' ') || '',
        email: profile?.email,
        phone: profile?.phone || '',
        stripeCustomerId: profile?.stripe_customer_id,
        billing: {
          address_line_1: '',
          address_line_2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'IN'
        }
      };

      setBookingData(bookingDetails);
      setCustomerInfo(customer);

      // Create payment intent
      await createPaymentIntent(bookingDetails, customer);

    } catch (err) {
      console.error('Booking initialization error:', err);
      setError(err?.message || 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const createPaymentIntent = async (booking, customer) => {
    try {
      const orderData = {
        items: booking?.items,
        subtotal: booking?.subtotal,
        tax_amount: booking?.taxAmount,
        service_fee: booking?.serviceFee,
        total: booking?.total,
        currency: booking?.currency,
        destination: booking?.destination,
        departure_date: booking?.departure_date,
        return_date: booking?.return_date,
        enquiry_id: booking?.enquiry_id
      };

      const paymentIntentResponse = await paymentService?.createPaymentIntent(orderData, customer);
      
      setClientSecret(paymentIntentResponse?.clientSecret);
      setPaymentIntentData(paymentIntentResponse);

    } catch (error) {
      console.error('Payment intent creation failed:', error);
      setError('Failed to initialize payment. Please try again.');
    }
  };

  const handlePaymentSuccess = (result) => {
    console.log('Payment successful:', result);
    
    // Navigate to success page with booking details
    const successParams = new URLSearchParams({
      booking_reference: result.orderNumber || paymentIntentData.orderNumber,
      order_id: result.orderId || paymentIntentData.orderId
    });
    
    navigate(`/booking-success?${successParams?.toString()}`);
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setError(error?.message || 'Payment failed. Please try again.');
  };

  const updateCustomerBilling = (billingInfo) => {
    setCustomerInfo(prev => ({
      ...prev,
      billing: { ...prev?.billing, ...billingInfo }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="animate-spin w-12 h-12 mx-auto">
                <AppIcon name="Loader2" size={48} className="text-blue-600" />
              </div>
              <p className="text-gray-600">Loading your booking details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 text-center">
            <AppIcon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Booking Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-4">
              <Button onClick={() => navigate(-1)} variant="outline">
                Go Back
              </Button>
              <Button onClick={() => navigate('/')} iconName="Home">
                Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
            <AppIcon name="ArrowLeft" size={16} />
            <button 
              onClick={() => navigate(-1)}
              className="hover:text-blue-600 transition-colors"
            >
              Back to Package
            </button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Complete Your Booking</h1>
          <p className="text-gray-600">Secure checkout for your travel adventure</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Booking Summary */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Booking Summary</h2>
              
              {/* Package Details */}
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                    <AppIcon name="MapPin" size={24} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{bookingData?.package?.name}</h3>
                    <p className="text-gray-600">{bookingData?.destination}</p>
                    <p className="text-sm text-gray-500">
                      {bookingData?.package?.duration_days} days • {bookingData?.variant?.name}
                    </p>
                  </div>
                </div>

                {/* Travel Dates */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Departure</p>
                      <p className="text-gray-900">
                        {bookingData?.departure_date ? new Date(bookingData.departure_date)?.toLocaleDateString() : 'TBD'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Return</p>
                      <p className="text-gray-900">
                        {bookingData?.return_date ? new Date(bookingData.return_date)?.toLocaleDateString() : 'TBD'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pricing Breakdown */}
                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Package cost × {travelers} traveler{travelers > 1 ? 's' : ''}
                    </span>
                    <span className="text-gray-900">
                      {paymentService?.formatAmount(bookingData?.subtotal, bookingData?.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service fee</span>
                    <span className="text-gray-900">
                      {paymentService?.formatAmount(bookingData?.serviceFee, bookingData?.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Taxes & fees</span>
                    <span className="text-gray-900">
                      {paymentService?.formatAmount(bookingData?.taxAmount, bookingData?.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold border-t border-gray-200 pt-2">
                    <span className="text-gray-900">Total</span>
                    <span className="text-blue-600">
                      {paymentService?.formatAmount(bookingData?.total, bookingData?.currency)}
                    </span>
                  </div>
                </div>

                {/* Package Includes */}
                {bookingData?.package?.includes && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-2">Included</h4>
                    <ul className="text-sm text-green-800 space-y-1">
                      {bookingData?.package?.includes?.map((item, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <AppIcon name="Check" size={14} className="text-green-600" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Traveler Information</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Primary Traveler</label>
                  <p className="text-gray-900">{customerInfo?.firstName} {customerInfo?.lastName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{customerInfo?.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Number of Travelers</label>
                  <p className="text-gray-900">{travelers} {travelers > 1 ? 'people' : 'person'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="space-y-6">
            <StripeProvider>
              <StripePaymentForm
                clientSecret={clientSecret}
                amount={bookingData?.total ? Math.round(bookingData?.total * 100) : 0}
                currency={bookingData?.currency}
                orderData={{
                  orderId: paymentIntentData?.orderId,
                  orderNumber: paymentIntentData?.orderNumber,
                  items: bookingData?.items
                }}
                customerInfo={customerInfo}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                confirmButtonText="Confirm Booking & Pay"
                className="lg:sticky lg:top-8"
              />
            </StripeProvider>

            {/* Trust Signals */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Why Book with HoliYay?</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <AppIcon name="Shield" size={20} className="text-green-600" />
                  <span className="text-sm text-gray-700">Secure payment processing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <AppIcon name="Clock" size={20} className="text-blue-600" />
                  <span className="text-sm text-gray-700">24/7 customer support</span>
                </div>
                <div className="flex items-center space-x-3">
                  <AppIcon name="Award" size={20} className="text-purple-600" />
                  <span className="text-sm text-gray-700">Best price guarantee</span>
                </div>
                <div className="flex items-center space-x-3">
                  <AppIcon name="RefreshCcw" size={20} className="text-orange-600" />
                  <span className="text-sm text-gray-700">Flexible cancellation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCheckout;